/*
 * One cache of object URLs for one article's images, shared by everything on the
 * page that shows them.
 *
 * WHY A STORE AND NOT A HOOK'S OWN REFS. The editor's preview and the Images
 * card's thumbnails both show an article's pictures, and each used to run its own
 * copy of the cache: the same 5 MB screenshot was fetched twice, concurrently,
 * whenever it was both referenced in the text and scrolled into view in the list.
 * The owner's rule (2026-09-21) is that an image is downloaded ONCE for the page
 * and re-used wherever it appears. So the cache moved out of the hook into this
 * object, which the page owns and every consumer registers with; the hook is now
 * a thin registration on top of it, and falls back to a private store of its own
 * when no page-level one is provided - the tests that pin its behaviour run it
 * that way.
 *
 * WHAT IT HOLDS. `fileName@contentHash` -> blob: URL. The hash is part of the key
 * on purpose: replacing an image's bytes under the same name is a new entry, so
 * the old URL is released and the new bytes fetched rather than the old picture
 * kept. Every URL holds its bytes in memory until revoked, which is why release
 * is as carefully handled as fetch.
 *
 * WHO WANTS WHAT. Each consumer hands in the set of entries it wants; the store
 * fetches the UNION and releases what no consumer wants any more - so a
 * thumbnail row scrolling out of view does not revoke a URL the preview is still
 * showing, and an image the preview drops stays as long as its row is on screen.
 *
 * THE RULES CARRIED OVER FROM THE HOOK, because each was a shipped defect:
 *
 *   - an entry in flight is not requested again when the wanted set changes;
 *     `inFlight` is the record of "asked for", separate from "arrived";
 *   - a released URL leaves the published record NOW, not when its replacement
 *     lands, or a caller building a NEW <img> from the record gets a dead URL;
 *   - bytes that arrive for an entry nobody wants any more - or after the store
 *     was disposed - are revoked on the spot, never cached;
 *   - a failed fetch leaves the entry absent AND not in flight, so it can be asked
 *     for again later;
 *   - each image is published as it arrives, not after the slowest.
 *
 * ANY OWNER, NOT ONLY AN ARTICLE (S42, 2026-10-12). The store used to hold the
 * admin api and an article id, and its one call to the server was the only thing
 * about it that knew what an article was. It now takes that call as a function
 * and an owner KEY (`article:<id>`, `release-note:<id>` - see imageOwner.ts), so
 * a release note's images are cached by exactly these rules; the name stayed,
 * since everything it holds is still an article-shaped image.
 */

/** `fileName@contentHash` - what a consumer wants, and what the cache is keyed on. */
export type ArticleImagePreviewEntry = string;

/** The entry for one image: its name and the hash of the bytes stored under it. */
export function articleImagePreviewEntry(fileName: string, contentHash: string): ArticleImagePreviewEntry {
  return `${fileName}@${contentHash}`;
}

function fileNameOf(entry: ArticleImagePreviewEntry): string {
  return entry.slice(0, entry.lastIndexOf('@'));
}

export class ArticleImagePreviewStore {
  private readonly cache = new Map<ArticleImagePreviewEntry, string>();
  private readonly inFlight = new Set<ArticleImagePreviewEntry>();
  private readonly wantedBy = new Map<symbol, ReadonlySet<ArticleImagePreviewEntry>>();
  private readonly listeners = new Set<() => void>();
  private snapshot: Record<string, string> = {};
  private disposed = false;

  /**
   * @param fetchObjectUrl the owner's bytes for one file name, as a blob: URL
   *   this store then owns and revokes.
   * @param ownerKey whose images these are - what a consumer compares with its
   *   own owner's key before it uses this store rather than one of its own.
   */
  constructor(
    private readonly fetchObjectUrl: (fileName: string) => Promise<string>,
    readonly ownerKey: string,
  ) {}

  /** For useSyncExternalStore. Stable per instance, so a consumer subscribes once. */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** File name -> object URL for everything held. The same object until something changes. */
  readonly getSnapshot = (): Record<string, string> => this.snapshot;

  /** Replace what one consumer wants. Fetches what is new, releases what nobody wants. */
  want(consumer: symbol, entries: ReadonlySet<ArticleImagePreviewEntry>): void {
    if (this.disposed) return;
    this.wantedBy.set(consumer, entries);
    this.reconcile();
  }

  /** A consumer has gone. What only it wanted is released. */
  forget(consumer: symbol): void {
    if (!this.wantedBy.delete(consumer) || this.disposed) return;
    this.reconcile();
  }

  /** The page has gone. Everything held is released, and late arrivals will be too. */
  dispose(): void {
    this.disposed = true;
    for (const url of this.cache.values()) URL.revokeObjectURL(url);
    this.cache.clear();
    this.wantedBy.clear();
    this.listeners.clear();
  }

  private wanted(): Set<ArticleImagePreviewEntry> {
    const all = new Set<ArticleImagePreviewEntry>();
    for (const entries of this.wantedBy.values()) for (const entry of entries) all.add(entry);
    return all;
  }

  private reconcile(): void {
    const next = this.wanted();

    let released = false;
    for (const [entry, url] of [...this.cache]) {
      if (next.has(entry)) continue;
      URL.revokeObjectURL(url);
      this.cache.delete(entry);
      released = true;
    }

    // Sorted, so a batch is requested in a stable order whatever the consumers'
    // registration order was.
    const missing = [...next]
      .filter((entry) => !this.cache.has(entry) && !this.inFlight.has(entry))
      .sort();

    if (released || missing.length === 0) this.publish();

    for (const entry of missing) {
      this.inFlight.add(entry);
      void this.fetch(entry);
    }
  }

  private async fetch(entry: ArticleImagePreviewEntry): Promise<void> {
    try {
      const url = await this.fetchObjectUrl(fileNameOf(entry));
      if (this.disposed || !this.wanted().has(entry)) {
        URL.revokeObjectURL(url);
        return;
      }
      this.cache.set(entry, url);
      this.publish();
    } catch {
      // Left absent on purpose - the consumer shows its own placeholder, and the
      // entry is free to be asked for again.
    } finally {
      this.inFlight.delete(entry);
    }
  }

  private publish(): void {
    this.snapshot = Object.fromEntries(
      [...this.cache].map(([entry, url]) => [fileNameOf(entry), url]),
    );
    for (const listener of [...this.listeners]) listener();
  }
}
